import React, { Fragment, memo } from 'react';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Avatar } from '@mui/material';
import { useGetResponsive } from '@stack/helpers/lib/responsive';
import ReactLightBox from 'yet-another-react-lightbox';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import Counter from 'yet-another-react-lightbox/plugins/counter';
import Fullscreen from 'yet-another-react-lightbox/plugins/fullscreen';
import Slideshow from 'yet-another-react-lightbox/plugins/slideshow';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import Video from 'yet-another-react-lightbox/plugins/video';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';

import LightboxCarouselProps from './props';

import 'yet-another-react-lightbox/plugins/captions.css';
import 'yet-another-react-lightbox/plugins/thumbnails.css';

/**
 * ===========================
 * MAIN
 * ===========================
 */
const LightboxCarouselView: React.FC<LightboxCarouselProps> = (props) => {
  const { playDuration = 3000, slides, ...lightboxProps } = props;

  // ========================= HOOKS
  const { isMobile } = useGetResponsive();

  // ========================= EVENTS

  // ========================= VARIABLES

  // ========================= EFFECTS

  // ========================= VIEWS
  return (
    <Fragment>
      <ReactLightBox
        plugins={[
          Fullscreen,
          Slideshow,
          Thumbnails,
          Video,
          Zoom,
          Captions,
          Counter,
        ]}
        counter={{
          container: {
            style: {
              top: 'unset',
              bottom: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              color: 'white',
              fontWeight: 600,
              fontSize: isMobile ? 18 : 24,
            },
          },
        }}
        styles={{
          root: {
            '--yarl__color_backdrop': 'rgba(0, 0, 0, 1)',
            '--yarl__button_filter': 'none',
            '--yarl__counter_filter': 'none',
            '--yarl__color_button': 'hsla(0,0%,100%,1)',
            '--yarl__color_button_disabled': 'hsla(0,0%,100%,.5)',
          },
          thumbnail: {
            background: 'none',
            aspectRatio: 186 / 124,
            borderColor: '#FFC800',
            borderWidth: 5,
            borderRadius: 0,
            padding: 0,
            ...(isMobile && {
              width: 85,
              height: 56,
            }),
          },
        }}
        thumbnails={{
          imageFit: 'cover',
          showToggle: true,
          gap: 10,
          vignette: false,
          borderStyle: 'none',
        }}
        slideshow={{
          autoplay: false,
          delay: playDuration,
        }}
        controller={{
          closeOnBackdropClick: true,
          closeOnPullDown: false,
          closeOnPullUp: false,
        }}
        animation={{
          fade: 500,
          swipe: 500,
        }}
        carousel={{
          finite: false,
          imageFit: 'contain',
          imageProps: {
            style: {
              padding: 4,
              aspectRatio: isMobile ? 1 : 930 / 525,
            },
          },
          ...(slides?.length && { preload: slides.length }),
        }}
        zoom={{
          maxZoomPixelRatio: 10,
          zoomInMultiplier: 1.5,
          doubleTapDelay: 300,
          doubleClickDelay: 500,
          doubleClickMaxStops: 100,
          keyboardMoveDistance: 10,
          wheelZoomDistanceFactor: 100,
          pinchZoomDistanceFactor: 100,
          scrollToZoom: true,
        }}
        render={{
          iconNext: () => (
            <Avatar sx={{ mr: { xs: -1.5, lg: 3, width: 40, height: 40 } }}>
              <ChevronRightIcon
                sx={{
                  width: 26,
                  height: 26,
                  color: '#1E242F',
                }}
              />
            </Avatar>
          ),
          iconPrev: () => (
            <Avatar sx={{ ml: { xs: -1.5, lg: 3, width: 40, height: 40 } }}>
              <ChevronLeftIcon
                sx={{
                  width: 26,
                  height: 26,
                  color: '#1E242F',
                }}
              />
            </Avatar>
          ),
        }}
        slides={slides}
        {...lightboxProps}
      />
    </Fragment>
  );
};

/**
 * ===========================
 * EXPORTS
 * ===========================
 */
export * from './props';
export const LightboxCarousel = memo(LightboxCarouselView);
export default LightboxCarousel;
